import { LowSync } from "lowdb";
import { LocalStoragePreset } from "lowdb/browser";
import { API_BASE_URL, LOCAL_LOWDB_NAME } from "../api/helpers";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import { defaultDatabase } from "../api/schema";
import { sameUtcDay, utcDayKey } from "../lib/calendarDay";
import { expandedRecurrenceInRange } from "../lib/recurrence";
import { LocationService } from "./LocationService";
import type { DatabaseSchema } from "../api/schema";
import type { Amenity, AmenityFilters, AmenitySort } from "@/types/Amenity";
import type { AmenityBooking } from "@/types/AmenityBooking";

export class AmenityService {
  private db: LowSync<DatabaseSchema> | null = null;

  private async initDB() {
    if (!this.db) {
      const db = LocalStoragePreset<DatabaseSchema>(LOCAL_LOWDB_NAME, defaultDatabase);
      this.db = db;
      db.read();
    }
    return this.db as LowSync<DatabaseSchema>;
  }

  private applyClientFilters(amenities: Amenity[], filters: AmenityFilters): Amenity[] {
    return amenities.filter((amenity) => {
      if (filters.amenityId && amenity.id !== filters.amenityId) return false;

      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        const nameMatch = amenity.name.toLowerCase().includes(searchLower);
        const tagsMatch = amenity.tags?.some((tag) => tag.toLowerCase().includes(searchLower));
        if (!tagsMatch && !nameMatch) {
          return false;
        }
      }
      return true;
    });
  }

  private sortAmenities(amenities: Amenity[], sort: AmenitySort): Amenity[] {
    const sorted = [...amenities];

    sorted.sort((a, b) => {
      let comparison = 0;

      switch (sort.field) {
        case "name":
          comparison = a.name.localeCompare(b.name);
          break;
        case "isBookable":
          comparison = Number(a.isBookable) - Number(b.isBookable);
          break;
      }

      return sort.order === "asc" ? comparison : -comparison;
    });

    return sorted;
  }

  async getAmenitiesForLocation(
    locationId: string,
    filters?: AmenityFilters,
    sort?: AmenitySort,
    includeHidden = false,
  ): Promise<Amenity[]> {
    const locationService = new LocationService();
    let _amenities = await locationService.getAmenitiesForLocation(locationId);

    // Filter out hidden amenities unless explicitly included
    if (!includeHidden) {
      _amenities = _amenities.filter((a) => !a.hidden);
    }

    if (filters) {
      _amenities = this.applyClientFilters(_amenities, filters);
    }

    if (sort) {
      _amenities = this.sortAmenities(_amenities, sort);
    }

    return _amenities || [];
  }

  async getBookableAmenitiesForLocation(
    locationId: string,
    filters?: AmenityFilters,
    sort?: AmenitySort,
    includeHidden = false,
  ): Promise<Amenity[]> {
    const _amenities = await this.getAmenitiesForLocation(locationId, filters, sort, includeHidden);
    return _amenities.filter((amenity) => amenity.isBookable);
  }

  async getAmenityById(locationId: string, amenityId: string): Promise<Amenity | null> {
    const locationService = new LocationService();
    const amenity = await locationService.getAmenityById(locationId, amenityId);
    return amenity;
  }

  async getBookableAmenitiesWithAvailability(
    locationId: string,
    date: Date,
    startTime: string,
    endTime: string,
  ): Promise<{ amenities: Amenity[]; bookedAmenityIds: Set<string> }> {
    const locationService = new LocationService();
    const amenities = await locationService.getAmenitiesForLocation(locationId);
    const bookableAmenities = amenities.filter((amenity) => amenity.isBookable && !amenity.hidden);

    // Get all amenity bookings for this location
    const db = await this.initDB();
    const allAmenityBookings = db.data.amenityBookings || [];

    // Expand recurring bookings and filter by date/time
    const bookedAmenityIds = new Set<string>();

    for (const booking of allAmenityBookings) {
      if (booking.locationId !== locationId) continue;

      let bookingDates: Date[] = [];

      // Handle recurring bookings
      if (booking.recurrence) {
        if (!booking.date) continue;
        const start = new Date(booking.date);
        // Step the range end forward one year in UTC — `expandedRecurrenceInRange` returns
        // UTC day-key strings, so the window bounding its expansion must stay UTC-anchored
        // too (local setFullYear/getFullYear can read a different "year" for west/east-of-UTC
        // viewers near a year boundary).
        const end = new Date(
          Date.UTC(
            start.getUTCFullYear() + 1,
            start.getUTCMonth(),
            start.getUTCDate(),
            start.getUTCHours(),
            start.getUTCMinutes(),
            start.getUTCSeconds(),
            start.getUTCMilliseconds(),
          ),
        );
        const occurrenceDateStrings = expandedRecurrenceInRange(booking as any, start, end);
        bookingDates = occurrenceDateStrings.map((d) => new Date(d));
      } else {
        bookingDates = booking.date ? [new Date(booking.date)] : [];
      }

      // Check if any occurrence conflicts with requested date/time. Match on UTC calendar day —
      // `bookingDate` originates from a UTC day-key ("YYYY-MM-DD") and `date`'s intended calendar
      // day is likewise UTC-anchored (see calendarDay.ts docblock), so local getFullYear/getMonth/
      // getDate comparison would misbucket the occurrence for viewers west/east of UTC.
      for (const bookingDate of bookingDates) {
        const isSameDay = sameUtcDay(bookingDate, date);

        if (isSameDay) {
          // Check time overlap
          const bookingStart = booking.startTime;
          const bookingEnd = booking.endTime;

          // Times overlap if: start1 < end2 && start2 < end1
          if (
            bookingStart !== undefined &&
            bookingEnd !== undefined &&
            startTime < bookingEnd &&
            bookingStart < endTime
          ) {
            bookedAmenityIds.add(booking.amenityId);
          }
        }
      }
    }

    // Return all bookable amenities and the set of booked IDs
    return { amenities: bookableAmenities, bookedAmenityIds };
  }

  /**
   * Check amenity availability for recurring bookings
   * @param locationId - The location ID
   * @param amenityIds - Array of amenity IDs to check
   * @param startDate - First occurrence date
   * @param startTime - Time in HH:mm format
   * @param endTime - Time in HH:mm format
   * @param occurrenceCount - Number of occurrences (e.g., 8 weeks)
   * @returns Map of amenityId to array of dates where that amenity is unavailable
   */
  async checkRecurringAmenityAvailability(
    locationId: string,
    amenityIds: string[],
    startDate: Date,
    startTime: string,
    endTime: string,
    occurrenceCount: number,
  ): Promise<Map<string, Date[]>> {
    const unavailabilityMap = new Map<string, Date[]>();

    // Initialize map with empty arrays
    amenityIds.forEach((id) => unavailabilityMap.set(id, []));

    // Generate occurrence dates (every week on the same day). Stepped in UTC — startDate is a
    // UTC-anchored calendar day (see calendarDay.ts), and local setDate/getDate stepping can land
    // on the wrong UTC day across a DST transition or for viewers west/east of UTC, which then
    // desyncs these occurrence keys from the UTC day-keys expandedRecurrenceInRange returns below.
    const occurrenceDates: Date[] = [];
    for (let i = 0; i < occurrenceCount; i++) {
      const occurrenceDate = new Date(
        Date.UTC(
          startDate.getUTCFullYear(),
          startDate.getUTCMonth(),
          startDate.getUTCDate() + i * 7,
          startDate.getUTCHours(),
          startDate.getUTCMinutes(),
          startDate.getUTCSeconds(),
          startDate.getUTCMilliseconds(),
        ),
      );
      occurrenceDates.push(occurrenceDate);
    }

    // Get all amenity bookings
    const db = await this.initDB();
    const allAmenityBookings = db.data.amenityBookings || [];

    // Check each occurrence date
    for (const occurrenceDate of occurrenceDates) {
      // Check which amenities are booked on this date
      const bookedOnThisDate = new Set<string>();

      for (const booking of allAmenityBookings) {
        if (booking.locationId !== locationId || !amenityIds.includes(booking.amenityId)) continue;

        let bookingDates: Date[] = [];

        // Handle recurring amenity bookings
        if (booking.recurrence && booking.date) {
          const start = new Date(booking.date);
          // See getBookableAmenitiesWithAvailability above: step the range end in UTC so it stays
          // consistent with the UTC day-keys expandedRecurrenceInRange returns.
          const end = new Date(
            Date.UTC(
              start.getUTCFullYear() + 1,
              start.getUTCMonth(),
              start.getUTCDate(),
              start.getUTCHours(),
              start.getUTCMinutes(),
              start.getUTCSeconds(),
              start.getUTCMilliseconds(),
            ),
          );
          const occurrenceDateStrings = expandedRecurrenceInRange(booking as any, start, end);
          bookingDates = occurrenceDateStrings.map((d) => new Date(d));
        } else if (booking.date) {
          bookingDates = [new Date(booking.date)];
        }

        // Check if this booking occurs on our occurrence date — matched on UTC calendar day (see
        // the isSameDay note above for why local getFullYear/getMonth/getDate is wrong here).
        for (const bookingDate of bookingDates) {
          const isSameDay = sameUtcDay(bookingDate, occurrenceDate);

          if (isSameDay && booking.startTime && booking.endTime) {
            // Check time overlap
            if (startTime < booking.endTime && booking.startTime < endTime) {
              bookedOnThisDate.add(booking.amenityId);
            }
          }
        }
      }

      // Add this date to unavailability for booked amenities
      bookedOnThisDate.forEach((amenityId) => {
        unavailabilityMap.get(amenityId)?.push(occurrenceDate);
      });
    }

    return unavailabilityMap;
  }

  /**
   * Get amenity bookings for a location within a date range
   * Fetches from the API and expands recurring bookings to individual occurrences
   * @param locationId - The location ID
   * @param startDate - Start date of the range (inclusive)
   * @param endDate - End date of the range (inclusive)
   * @returns Array of amenity bookings with expanded recurrences
   */
  async getAmenityBookingsForLocationAndDateRange(
    locationId: string,
    startDate: Date,
    endDate: Date,
  ): Promise<AmenityBooking[]> {
    try {
      // Format as UTC calendar days (YYYY-MM-DD), not date-fns' local `format` — startDate/endDate
      // are UTC-anchored calendar-day bounds (see BrickWeekSchedule/BrickDayScheduleAlt), and a
      // local-TZ format could shift the requested range onto the wrong day(s) for the server query.
      const startDateString = utcDayKey(startDate);
      const endDateString = utcDayKey(endDate);

      // Fetch amenity bookings from the API
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/amenity-bookings?locationId=${locationId}&startDate=${startDateString}&endDate=${endDateString}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch amenity bookings: ${response.statusText}`);
      }

      const data = await response.json();
      const allAmenityBookings: AmenityBooking[] = data.data || [];

      // Parse date strings back to Date objects
      const bookingsWithParsedDates = allAmenityBookings.map((booking) => ({
        ...booking,
        date: booking.date ? new Date(booking.date) : undefined,
      }));

      const expandedBookings: AmenityBooking[] = [];

      for (const booking of bookingsWithParsedDates) {
        // Handle recurring bookings
        if (booking.recurrence && booking.date) {
          // Expand recurrences within the requested range
          const occurrenceDateStrings = expandedRecurrenceInRange(booking as any, startDate, endDate);

          // Create a booking instance for each occurrence
          for (const occurrenceDateString of occurrenceDateStrings) {
            const occurrenceDate = new Date(occurrenceDateString);
            expandedBookings.push({
              ...booking,
              date: occurrenceDate,
              recurrencePattern: booking.recurrence, // Store original pattern
              recurrence: undefined, // Individual occurrences don't have recurrence
            });
          }
        } else {
          // Non-recurring booking - already within range since API filtered it
          expandedBookings.push(booking);
        }
      }

      return expandedBookings;
    } catch (error) {
      console.error("Error fetching amenity bookings:", error);
      return [];
    }
  }
}
